import { fetchAuthSession, getCurrentUser, type GetCurrentUserOutput } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useEffect, useState } from 'react';

import { createSessionApiThunk, logoutUserThunk } from '../features/auth/api/authSlice';
import { useAppDispatch } from '../store';
import alert from '../utils/alert';
import { verifyAllSessionCookies } from '../utils/sessionCookies';
import { disconnect } from '../store/socket';
import useMfaEnabledFlag from './useIsMfaEnabledFlag';
import { rolesFromFHIR4Identifier } from '../utils/general';
import { SYSTEM_ROLES } from '../config';

type AuthStatus = 'signedIn' | 'signedOut' | undefined;
export interface UseAuthResponse {
  isAuthenticating: boolean;
  currentUser: GetCurrentUserOutput | null;
  authStatus: AuthStatus;
}

const getCurrentUserInfo = async (): Promise<GetCurrentUserOutput | null> => {
  try {
    return await getCurrentUser();
  } catch {
    return null;
  }
};

export const checkIfUserHasPermission = (identifiers: string): boolean => {
  const identifiersArray: fhir4.Identifier[] = JSON.parse(identifiers);

  const userRoles = rolesFromFHIR4Identifier(identifiersArray || []);

  return userRoles.some((role) => [SYSTEM_ROLES.PATIENT].includes(role));
};

// Documentation for Hub events:
// https://docs.amplify.aws/lib/auth/auth-events/q/platform/js/
const useAuth = (config?: {
  onAuthStatusChange?: (authStatus: AuthStatus) => void;
}): UseAuthResponse => {
  const dispatch = useAppDispatch();
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<GetCurrentUserOutput | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(() =>
    verifyAllSessionCookies() ? 'signedIn' : 'signedOut',
  );

  const isMfaEnabled = useMfaEnabledFlag();

  useEffect(() => {
    if (!isMfaEnabled) {
      getCurrentUserInfo().then(setCurrentUser);
    }
  }, [isMfaEnabled]);

  useEffect(() => {
    const updateUser = async (data?: any) => {
      if (data) {
        const status = data.payload.event;
        if ((status === 'signedIn' || status === 'signedOut') && status !== authStatus) {
          setIsAuthenticating(true);
          if (status === 'signedIn') {
            try {
              if (!isMfaEnabled) {
                const cognitoUser: GetCurrentUserOutput = data.payload.data;
                const { accessToken } = (await fetchAuthSession()).tokens ?? {};
                const cognitoAccessToken = accessToken?.toString();
                await dispatch(
                  createSessionApiThunk({
                    accessToken: cognitoAccessToken || '',
                  }),
                ).unwrap();
                setCurrentUser(cognitoUser);
              }
              setAuthStatus(status);
              config?.onAuthStatusChange?.(status);
            } catch (err: any) {
              alert.error(err?.error || 'Something went wrong, cannot login.', {
                toastId: 'general-login-error',
              });
              await dispatch(logoutUserThunk());
              // disconnect websockets
              dispatch(disconnect());
            }
          } else {
            setCurrentUser(null);
            setAuthStatus(status);
            // disconnect websockets
            dispatch(disconnect());
            config?.onAuthStatusChange?.(status);
          }
          setIsAuthenticating(false);
        }
      }
    };
    const hubListenerCancelToken = Hub.listen('auth', updateUser);
    return () => hubListenerCancelToken();
  }, [authStatus, config, dispatch, isMfaEnabled]);

  return { isAuthenticating, currentUser, authStatus };
};

export default useAuth;
export { getCurrentUserInfo as getCurrentUser };
